<template>
  <div class="superadmin">
    <router-view />
    <SearchBar :search-word="searchWord" />
    <div v-if="tenants" class="content--main">
      <div v-if="tenantsLoading">
        Loading...
      </div>
      <div v-if="!tenantsLoading && tenants.length === 0" class="row">
        <div class="col-6 length-0">
          <span>No tenants found</span>
        </div>
      </div>
      <div v-else class="row">
        <div class="col-4">
          <div class="tenant__list">
            <div v-for="(tenant, i) of tenants" :key="i">
              <Widget
                :widgets="tenant"
                :class="{
                  active: i === activeIndex,
                  'ribbon ribbon--danger': !!tenant.deletedAt
                }"
                @widgetClick="handleWidgetClick(i, tenant)"
              >
                <div class="widget--avatar">
                  <img
                    v-if="tenant.avatar"
                    :src="tenant.avatar"
                    alt="user avatxar"
                  />
                  <span v-else>{{ tenant.name.substring(0, 2) }}</span>
                </div>
                <div class="widget--info">
                  <p class="mb-0">
                    {{ tenant.name }}
                  </p>
                  <p class="muted mb-0">
                    {{ tenant.id }}
                  </p>
                </div>
              </Widget>
            </div>
          </div>
        </div>
        <div v-if="singleTenant" class="col-8 tenant__profile-wrapper">
          <div class="tenant__profile">
            <WidgetDetails
              title="Tenant"
              :widget-options="widgetOptions"
              :tenant="singleTenant"
              @deleteTenant="handleDeleteTenantClick"
              @restoreTenant="handleRestoreTenantClick"
              @editTenantDetails="handleEditTenantClick"
            >
              <div class="block-content">
                <div class="d-flex">
                  <div v-if="singleTenant.avatar" class="widget--avatar">
                    <img :src="singleTenant.avatar" alt="user avatar" />
                  </div>
                  <div v-else class="widget--avatar">
                    <span>{{ singleTenant.name.substring(0, 2) }}</span>
                  </div>
                  <div class="widget--details-wrapper">
                    <h4>{{ singleTenant.name }}</h4>
                    <span
                      v-if="!!singleTenant.deletedAt"
                      class="badge badge-pill badge-danger mb-2"
                    >
                      Deactivated
                    </span>
                    <p class="-muted flex-column">
                      <span>
                        <span>{{ singleTenant.id }}.headroomapp.com</span>
                        <a
                          :href="
                            'https://' + singleTenant.id + '.headroomapp.com'
                          "
                          target="_blank"
                          class="ml-1"
                        >
                          <svg
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 576 512"
                            width="16"
                          >
                            <!-- eslint-disable-next-line -->
                            <path fill="currentColor" d="M576 24v127.984c0 21.461-25.96 31.98-40.971 16.971l-35.707-35.709-243.523 243.523c-9.373 9.373-24.568 9.373-33.941 0l-22.627-22.627c-9.373-9.373-9.373-24.569 0-33.941L442.756 76.676l-35.703-35.705C391.982 25.9 402.656 0 424.024 0H552c13.255 0 24 10.745 24 24zM407.029 270.794l-16 16A23.999 23.999 0 0 0 384 303.765V448H64V128h264a24.003 24.003 0 0 0 16.97-7.029l16-16C376.089 89.851 365.381 64 344 64H48C21.49 64 0 85.49 0 112v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V287.764c0-21.382-25.852-32.09-40.971-16.97z" class=""></path>
                          </svg>
                        </a>
                      </span>

                      <span v-if="singleTenant.ownerEmail">
                        {{ singleTenant.ownerEmail }}
                        <a
                          :href="`mailto:${singleTenant.ownerEmail}`"
                          target="_blank"
                          class="ml-1"
                        >
                          <!-- eslint-disable-next-line -->
                          <svg role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16">
                            <!-- eslint-disable-next-line -->
                            <path fill="currentColor" d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z" class=""></path>
                          </svg>
                        </a>
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <UserList
                @addTenantUser="handleAddTenantUserClick"
                @editTenantUser="handleEditTenantUserClick"
                @tenantUserMore="handleOnTenantUserMoreClick"
                @viewTenantUser="handleOnTenantUserViewClick"
                :tenant="singleTenant"
                :roles="roles"
              />
            </WidgetDetails>
          </div>
        </div>
      </div>
    </div>
    <Spinner v-else class="mt-3" :theme="primary" />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import SearchBar from "@/modules/superadmin/elements/SearchBar";
import Spinner from "@/components/Spinner";
import Widget from "@/modules/superadmin/elements/Widget";
import WidgetDetails from "@/modules/superadmin/elements/WidgetDetails";
import UserList from "@/modules/superadmin/elements/UserList";
import axios from "@/helpers/base";
import * as logger from "@/helpers/logger";

export default {
  name: "SuperadminDashboard",
  components: {
    SearchBar,
    Spinner,
    Widget,
    WidgetDetails,
    UserList
  },
  data: () => ({
    searchWord: null,
    activeIndex: 0,
    roles: [],
    tenants: [],
    tenantsLoading: true,
    singleTenant: null,
    widgetOptions: [
      {
        icon: "edit",
        label: "Edit Details",
        method: "editTenantDetails"
      },
      {
        icon: "deactivate",
        label: "Deactivate",
        method: "deleteTenant"
      },
      {
        icon: "activate",
        label: "Activate",
        method: "restoreTenant"
      }
    ]
  }),
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    }
  },
  watch: {
    activeIndex(nextActiveIndex) {
      this.handleFetchRoles(this.tenants[nextActiveIndex].id).then(roles => {
        this.roles = roles;
      });
      this.$nextTick(() => {
        this.handleScrollView();
      });
    }
  },
  async mounted() {
    await this.handleFetchTenants();
    await this.handleFetchRoles(this.tenants[this.activeIndex].id);
    this.singleTenant = this.tenants[this.activeIndex];
    await this.handleWidgetClick(this.activeIndex, this.singleTenant);

    this.$root.$on("superadmin:fetchTenants", async (activeIndex = 0) => {
      await this.handleFetchTenants();
      this.singleTenant = this.tenants[activeIndex];
      this.handleScrollView();
      await this.handleFetchRoles(this.tenants[activeIndex].id);
      await this.handleWidgetClick(activeIndex, this.singleTenant);
    });
  },
  beforeDestroy() {
    // Clean up event listeners.
    this.$root.$off("superadmin:fetchTenants");
  },
  methods: {
    ...mapActions({
      changeActiveTrail: "changeActiveTrail"
    }),
    async handleWidgetClick(index, tenant) {
      const { matched, path } = this.$route;
      const { name } = tenant;
      this.activeIndex = index;
      this.singleTenant = tenant;
      this.changeActiveTrail([
        { name: "Home", link: matched[0].path },
        { name: "Tenants", link: path },
        { name: `${name}` }
      ]);
    },
    handleDeleteTenantClick() {
      this.$swal({
        title: "Do you want to deactivate this tenant?",
        type: "warning",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        showCancelButton: true
      }).then(result => {
        if (result.dismiss) {
          return;
        }

        const remove = axios.delete(`/tenants/${this.singleTenant.id}`);
        remove
          .then(() => {
            this.handleFetchTenants();
          })
          .then(() => {
            this.$toastr.warning(
              `${this.singleTenant.name} has been deactivated.`
            );
          })
          .then(() => {
            this.activeIndex = Math.min(
              this.tenants.length,
              Math.max(0, this.activeIndex - 1)
            );
            this.singleTenant = this.tenants[this.activeIndex];
          });
      });
    },
    handleEditTenantClick() {
      this.$emit("showModal", {
        modal: "edit-tenant",
        name: "edit-tenant",
        singleRecord: this.singleTenant,
        activeIndex: this.activeIndex
      });
    },
    handleRestoreTenantClick() {
      this.$swal({
        title: "Do you want to activate this tenant?",
        type: "warning",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        showCancelButton: true
      }).then(result => {
        if (result.dismiss) {
          return;
        }

        const res = axios.patch(`/tenants/${this.singleTenant.id}`, {
          restore: true
        });

        res
          .then(() => {
            this.handleFetchTenants();
          })
          .then(() => {
            this.$toastr.success(
              `${this.singleTenant.name} has been activated.`
            );
            this.$nextTick(() => {
              this.activeIndex = Math.min(
                this.tenants.length,
                Math.max(0, this.activeIndex - 1)
              );
              this.singleTenant = this.tenants[this.activeIndex];
            });
          });
      });
    },
    handleAddTenantUserClick() {
      this.$emit("showModal", {
        modal: "tenant-add-user",
        name: "tenant-add-user",
        singleRecord: this.singleTenant,
        roles: this.roles
      });
    },
    handleEditTenantUserClick(payload) {
      this.$emit("showModal", {
        ...payload,
        modal: "tenant-edit-user",
        name: "tenant-edit-user",
        roles: this.roles
      });
    },
    handleOnTenantUserViewClick(payload) {
      this.$emit("showModal", {
        ...payload,
        modal: "tenant-view-user",
        name: "tenant-view-user"
      });
    },
    handleOnTenantUserMoreClick(payload) {
      logger.log("[SuperadminDashboard.vue]", payload);
    },
    async handleFetchTenants() {
      this.tenantsLoading = true;
      const params = new URLSearchParams();
      params.append("paranoid", false);
      const { data } = await axios.get(`/tenants?${params.toString()}`);
      this.tenants = data.data;
      this.tenantsLoading = false;
    },
    async handleFetchRoles(slug) {
      const { data } = await axios.get("/roles", {
        headers: {
          "X-Request-Origin": `${slug}.headroomapp.com`
        }
      });

      return (this.roles = data.data);
    },
    handleScrollView() {
      const el = document.querySelector(".widget.active");
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.superadmin {
  padding: 0 15px;
  .content--main {
    height: calc(
      100vh - 274px
    ); /* height of viewport minus the sum of nav.header and <SearchBar/> */
    overflow-x: hidden;
    overflow-y: auto;
    padding-bottom: 20px;
    padding-top: 20px;
    & > .row {
      height: 100%;
      div[class^="col-"] {
        height: 100%;
        overflow: auto;
      }
    }
    .tenant {
      &__list {
        border-radius: 5px;
        overflow: auto;
        height: 100%;
      }
      &__profile {
        border-radius: 5px;
        max-height: 100%;
        overflow: auto;
        .widget--avatar {
          margin-right: 30px;
          span {
            align-items: center;
            background-color: $brand-primary;
            border-radius: 50%;
            color: $white;
            display: flex;
            font-family: "Gotham";
            font-size: $font-size-xl;
            font-weight: 500;
            height: 110px;
            justify-content: center;
            width: 110px;
          }
          img {
            border-radius: 50%;
            display: block;
            height: 110px;
            object-fit: cover;
            width: 110px;
          }
        }
        .widget--details-wrapper {
          flex: auto;
          h4 {
            color: $gray-900;
            font-size: $font-size-md;
            font-weight: 500;
            margin-bottom: 10px;
          }
          p {
            align-items: flex-start;
            display: flex;
            font-family: "Gotham Book";
            font-size: $font-size-sm;
            img {
              margin-right: 10px;
              height: 20px;
              width: 20px;
            }
            &.-muted {
              color: $gray-550;
            }
          }
        }
        .widget--table-row {
          border-bottom: 1px solid $gray-250;
          padding: 10px 0;
          h4 {
            color: $gray-900;
            font-size: $font-size-xs;
            font-weight: 500;
          }
        }
        .length-0 {
          span {
            color: $gray-900;
            font-family: "Gotham Book";
            font-size: $font-size-sm;
            text-align: left;
          }
        }
        .table {
          font-size: $font-size-xs;
          thead {
            th {
              color: $gray-900;
              font-weight: 500;
            }
          }
          tbody {
            td {
              color: $gray-900;
              font-family: "Gotham Book";
            }
          }
        }
      }
    }
    .length-0 {
      span {
        color: $gray-900;
        font-family: "Gotham Book";
        font-size: $font-size-sm;
        text-align: left;
      }
    }
  }
}
</style>
